<template>
    <div id="tags-view-container" class="tags-view-container">
        <scroll-pane ref="scrollPaneRef" class="tags-view-wrapper" @scroll="handleScroll">
            <router-link
                v-for="tag in visitedViews"
                :key="tag.fullPath"
                :data-path="tag.fullPath"
                :class="isActive(tag) ? 'active' : ''"
                :to="tagToRoute(tag)"
                class="tags-view-item"
                @click.middle="!isAffix(tag) ? closeSelectedTag(tag) : ''"
                @contextmenu.prevent="openMenu(tag, $event)"
            >
                {{ tag.title }}
                <span
                    v-if="!isAffix(tag)"
                    class="tags-view-close-wrap"
                    @click.prevent.stop="closeSelectedTag(tag)"
                >
                    <close class="tags-view-close" />
                </span>
            </router-link>
        </scroll-pane>
        <Teleport to="body">
            <ul v-show="visible" :style="{ left: left + 'px', top: top + 'px' }" class="tags-view-contextmenu">
                <li @click="refreshSelectedTag(selectedTag)">
                    <refresh-right style="width: 1em; height: 1em" /> 刷新页面
                </li>
                <li v-if="!isAffix(selectedTag)" @click="closeSelectedTag(selectedTag)">
                    <close style="width: 1em; height: 1em" /> 关闭当前
                </li>
                <li @click="closeOthersTags"><circle-close style="width: 1em; height: 1em" /> 关闭其他</li>
                <li v-if="!isFirstView()" @click="closeLeftTags">
                    <back style="width: 1em; height: 1em" /> 关闭左侧
                </li>
                <li v-if="!isLastView()" @click="closeRightTags">
                    <right style="width: 1em; height: 1em" /> 关闭右侧
                </li>
                <li @click="closeAllTags(selectedTag)">
                    <circle-close style="width: 1em; height: 1em" /> 全部关闭
                </li>
            </ul>
        </Teleport>
    </div>
</template>

<script setup lang="ts">
/**
 * 标签页导航（对齐 EB layout/navBars/tagsView）
 */
import ScrollPane from './ScrollPane.vue';
import { getNormalPath, getTagPathKey } from '@/utils/http/mei-mei';
import useTagsViewStore from '@/store/modules/tagsView';
import { roterPre } from '@/settings';
import { MERCHANT_DASHBOARD_PATH } from '@/config/merchant-route';

import {
    ref,
    getCurrentInstance,
    ComponentInternalInstance,
    computed,
    onMounted,
    watch,
    nextTick,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';

const visible = ref(false);
const top = ref(0);
const left = ref(0);
const selectedTag = ref<any>({});
const affixTags = ref<any[]>([]);
const scrollPaneRef = ref<any>(null);

const { proxy } = getCurrentInstance() as ComponentInternalInstance;
const route = useRoute();
const router = useRouter();

const visitedViews = computed(() => useTagsViewStore().visitedViews);

watch(route, () => {
    addTags();
    moveToCurrentTag();
});
watch(visible, value => {
    if (value) {
        document.body.addEventListener('click', closeMenu);
    } else {
        document.body.removeEventListener('click', closeMenu);
    }
});
onMounted(() => {
    const store = useTagsViewStore();
    store.restoreVisitedViews();
    normalizeCrmebTags();
    addTags();
});

function isActive(r: any) {
    return r.fullPath === route.fullPath;
}
function tagToRoute(tag: any) {
    return { path: tag.path, query: tag.query, fullPath: tag.fullPath } as any;
}
function isAffix(tag: any) {
    return tag.meta && tag.meta.affix;
}
function isFirstView() {
    try {
        const fp = selectedTag.value.fullPath;
        const homePaths = ['/index', MERCHANT_DASHBOARD_PATH];
        return homePaths.includes(fp) || fp === visitedViews.value[1]?.fullPath;
    } catch (err) {
        return false;
    }
}
function isLastView() {
    try {
        return selectedTag.value.fullPath === visitedViews.value[visitedViews.value.length - 1].fullPath;
    } catch (err) {
        return false;
    }
}
/** EB 壳：清理拼错路径 / 旧「首页」标签 */
function normalizeCrmebTags() {
    const store = useTagsViewStore();
    const prefix = roterPre || '/merchant';
    const dashboardPath = MERCHANT_DASHBOARD_PATH;
    const doublePrefix = `${prefix}${prefix}/`;
    const seen = new Set<string>();

    store.visitedViews = store.visitedViews
        .map((v) => {
            const rawFull = String((v as any).fullPath || v.path || '');
            const fullPath = getNormalPath(rawFull);
            const path = getTagPathKey(rawFull);
            return { ...v, path, fullPath };
        })
        .filter((v) => {
            const pathKey = getTagPathKey(String((v as any).fullPath || v.path || ''));
            const fp = String((v as any).fullPath || v.path || '');
            if (!pathKey || !fp || fp.startsWith('//')) return false;
            if (!pathKey.startsWith(prefix)) return false;
            if (pathKey.includes(doublePrefix)) return false;
            if (seen.has(pathKey)) return false;
            seen.add(pathKey);
            return true;
        })
        .map((v) => {
            const fp = String((v as any).fullPath || v.path || '');
            if (fp === dashboardPath || fp.startsWith(`${dashboardPath}?`)) {
                return {
                    ...v,
                    meta: { ...(v.meta || {}), title: '控制台', affix: false },
                    title: '控制台',
                } as typeof v;
            }
            const title = String((v as any).title || (v.meta as any)?.title || '');
            if (title === '首页' && !fp.includes('/dashboard')) {
                return null;
            }
            return v;
        })
        .filter(Boolean) as typeof store.visitedViews;

    store.saveVisitedViews();
}
function addTags() {
    const { name } = route;
    if (name) {
        useTagsViewStore().addView(route);
        if (route.meta.link) {
            useTagsViewStore().addIframeView(route);
        }
    }
    return false;
}
function moveToCurrentTag() {
    nextTick(() => {
        const found = visitedViews.value.find(r => r.fullPath === route.fullPath);
        if (found) {
            scrollPaneRef.value!.moveToTarget(found);
            useTagsViewStore().updateVisitedView(route);
        }
    });
}
function refreshSelectedTag(view: any) {
    proxy!.$tab.refreshPage(view);
    if (route.meta.link) {
        useTagsViewStore().delIframeView(route);
    }
}
function closeSelectedTag(view: any) {
    proxy!.$tab.closePage(view).then(({ visitedViews }) => {
        if (isActive(view)) {
            toLastView(visitedViews, view);
        }
    });
}
function closeRightTags() {
    proxy!.$tab.closeRightPage(selectedTag.value).then((visitedViews: any) => {
        if (!visitedViews.find((i: any) => i.fullPath === route.fullPath)) {
            toLastView(visitedViews);
        }
    });
}
function closeLeftTags() {
    proxy!.$tab.closeLeftPage(selectedTag.value).then((visitedViews: any) => {
        if (!visitedViews.find((i: any) => i.fullPath === route.fullPath)) {
            toLastView(visitedViews);
        }
    });
}
function closeOthersTags() {
    router.push(selectedTag.value).catch(e => {
        // console.log(e);
    });
    proxy!.$tab.closeOtherPage(selectedTag.value).then(() => {
        moveToCurrentTag();
    });
}
function closeAllTags(view: any) {
    proxy!.$tab.closeAllPage().then(({ visitedViews }: any) => {
        if (affixTags.value.some(tag => tag.path === route.path)) {
            return;
        }
        toLastView(visitedViews, view);
    });
}
function toLastView(visitedViews: any, view?: any) {
    const latestView = visitedViews.slice(-1)[0];
    if (latestView) {
        router.push(latestView.fullPath);
    } else {
        // now the default is to redirect to the home page if there is no tags-view,
        // you can adjust it according to your needs.
        if (view?.name === 'Index' || view?.name === 'Dashboard') {
            router.replace({ path: '/redirect' + MERCHANT_DASHBOARD_PATH });
        } else {
            router.push(MERCHANT_DASHBOARD_PATH);
        }
    }
}
function openMenu(tag: any, e: MouseEvent) {
    const menuMinWidth = 130;
    const clientWidth = document.documentElement.clientWidth;
    let l = e.clientX;
    if (l + menuMinWidth > clientWidth) {
        l = clientWidth - menuMinWidth - 5;
    }
    left.value = l;
    top.value = e.clientY;
    visible.value = true;
    selectedTag.value = tag;
}
function closeMenu() {
    visible.value = false;
}
function handleScroll() {
    closeMenu();
}
</script>

<style lang="scss" scoped>
/* 对齐 EB layout/navBars/tagsView（tags-style-five 细节见 eb-layout.scss） */
.tags-view-container {
    height: 34px;
    width: 100%;
    background: var(--prev-bg-white, #fff);
    box-shadow: 0 1px 4px rgba(113, 128, 165, 0.1);
    position: relative;
    z-index: 10;

    .tags-view-wrapper {
        .tags-view-item {
            display: inline-flex;
            align-items: center;
            position: relative;
            cursor: pointer;
            height: 26px;
            line-height: 26px;
            border: 1px solid #ebeef5;
            color: var(--prev-color-text-regular, #303133);
            background: var(--prev-bg-white, #fff);
            padding: 0 12px 0 15px;
            font-size: 12px;
            margin-right: 5px;
            margin-top: 4px;
            border-radius: 2px;
            text-decoration: none;
            transition: all 0.3s cubic-bezier(0.2, 1, 0.3, 1);

            &:first-of-type {
                margin-left: 15px;
            }
            &:last-of-type {
                margin-right: 15px;
            }
        }
    }

}
</style>

<style lang="scss">
/* Teleport 到 body，须用全局类名（对齐 EB position: fixed + clientX/clientY） */
.tags-view-contextmenu {
    margin: 0;
    background: #fff;
    z-index: 3000;
    position: fixed;
    list-style-type: none;
    padding: 5px 0;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 400;
    color: #333;
    box-shadow: 2px 2px 3px 0 rgba(0, 0, 0, 0.3);
    li {
        margin: 0;
        padding: 7px 16px;
        cursor: pointer;
        &:hover {
            background: #eee;
        }
    }
}

.tags-view-wrapper {
    .tags-view-item {
        .tags-view-close-wrap {
            display: inline-flex;
            align-items: center;
            margin-left: 5px;
        }
        .tags-view-close {
            width: 14px;
            height: 14px;
            border-radius: 100%;
            transition: all 0.3s cubic-bezier(0.2, 1, 0.3, 1);
        }
        .tags-view-close-wrap:hover .tags-view-close {
            background-color: #b4bccc;
            color: #fff;
        }
    }
}
</style>
